import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import {
  HardDrive, Plus, Search, Trash2, Edit2, X, Save, AlertTriangle,
  Wrench, Calendar, Radiation, ChevronDown, FileText, MapPin, ClipboardCheck,
  CheckCircle2, Upload, Loader2,
} from 'lucide-react';
import Header from '../components/Header';
import DatePickerField from '../components/DatePickerField';
import EvidenceUploads from '../components/EvidenceUploads';
import BulkActions from '../components/BulkActions';
import PDFIntakeDropzone from '../components/PDFIntakeDropzone';
import { SECTIONS } from '../lib/sectionTheme';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const E = SECTIONS.equipment;

const MODALITIES = ['CT', 'Ultrasound', 'X-ray', 'DEXA', 'OPG', 'Fluoroscopy', 'MRI', 'Mammography', 'Other'];
const STATUSES = ['active', 'under_service', 'retired', 'decommissioned'];
const MOD_COLORS = {
  CT: '#2563EB', 'X-ray': '#DC2626', Ultrasound: '#0891B2', DEXA: '#16A34A',
  OPG: '#7C3AED', Fluoroscopy: '#D97706', MRI: '#0E7490', Mammography: '#EC4899', Other: '#64748B',
};

export default function EquipmentPage({ embedded = false }) {
  const { user } = useAuth();
  const isPM = user?.role === 'admin' || user?.role === 'superuser';
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [qaSummary, setQaSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [activeMod, setActiveMod] = useState('');
  const [editing, setEditing] = useState(null); // 'new' | object

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeMod) params.append('modality', activeMod);
      const [i, s, q2] = await Promise.all([
        axios.get(`${API}/equipment?${params.toString()}`, { withCredentials: true }),
        axios.get(`${API}/equipment/stats`, { withCredentials: true }),
        axios.get(`${API}/qa-tests/summary`, { withCredentials: true }),
      ]);
      setItems(i.data); setStats(s.data); setQaSummary(q2.data);
    } catch { /* */ }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeMod]);

  const filtered = q.trim()
    ? items.filter(r => `${r.asset_name} ${r.manufacturer} ${r.model} ${r.serial_number} ${r.location}`.toLowerCase().includes(q.toLowerCase()))
    : items;

  const onDelete = async (r) => {
    if (!window.confirm(`Delete "${r.asset_name}"? This also removes its service log.`)) return;
    await axios.delete(`${API}/equipment/${r.asset_id}`, { withCredentials: true });
    load();
  };

  // When `embedded` is true, this page is rendered inside CompliancePage as
  // a tab — skip our own Header + outer min-h-screen wrapper + the big
  // hero title (the parent already provides them). The data-testid is
  // preserved on the fragment-replacement div so prior smoke tests still pass.
  const inner = (
    <div data-testid="equipment-page" className="contents">
        {!embedded && (
        /* Header — title left, actions right (ReferralBase pattern) */
        <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: E.colorBg, color: E.color }}>
              <HardDrive className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Equipment Register · DIAS Entry-level</span>
            </div>
            <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold text-foreground">Practice equipment</h1>
            <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl">
              Every clinical and diagnostic device with serial numbers, sub-components, service history and RSO/licence linkage. The audit-ready single source of truth.
            </p>
          </div>
          {isPM && (
            <BulkActions
              resource="equipment"
              title="Equipment register"
              addLabel="Add equipment"
              onAdd={() => setEditing('new')}
              color={E.color}
              onAfterImport={load}
            />
          )}
          {!isPM && (
            <BulkActions
              resource="equipment"
              title="Equipment register"
              color={E.color}
            />
          )}
        </div>
        )}
        {embedded && isPM && (
          <div className="mb-4 flex justify-end">
            <BulkActions
              resource="equipment"
              title="Equipment register"
              addLabel="Add equipment"
              onAdd={() => setEditing('new')}
              color={E.color}
              onAfterImport={load}
            />
          </div>
        )}

        {/* KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6" data-testid="equipment-stats">
            <Kpi label="Total assets" value={stats.total} color={E.color} />
            <Kpi label="Active" value={stats.by_status?.active || 0} color="#16A34A" />
            <Kpi label="Service overdue" value={stats.overdue_service} color={stats.overdue_service > 0 ? '#DC2626' : '#64748B'} />
            <Kpi label="Service due ≤30d" value={stats.due_soon} color={stats.due_soon > 0 ? '#D97706' : '#64748B'} />
            <Kpi label="QA overdue" value={qaSummary?.overdue ?? 0} color={(qaSummary?.overdue ?? 0) > 0 ? '#DC2626' : '#64748B'} />
            <Kpi label="RSO-required" value={stats.rso_required} color="#7C3AED" />
          </div>
        )}

        {/* LSPN reconcile — admin-only. Drop a fresh export from the LSPN
            portal, GPT-5.2 fuzzy-matches against existing assets, dry-run
            preview shows the diff, and one click applies. */}
        {isPM && <LspnReconcileBar onApplied={load} />}

        {/* Filters */}
        <div className="bg-card border border-border rounded-2xl p-4 mb-5" data-testid="equipment-filters">
          <div className="flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-muted-foreground" />
            <input type="search" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search by asset, manufacturer, model, serial, location…"
              data-testid="equipment-search"
              className="flex-1 bg-transparent border-0 outline-none text-sm text-foreground placeholder:text-muted-foreground" />
            {q && <button onClick={() => setQ('')} className="text-xs text-muted-foreground hover:text-foreground">clear</button>}
          </div>
          <div className="flex items-center gap-1.5 flex-wrap border-t border-border pt-3">
            <Pill label={`All · ${stats?.total ?? '—'}`} active={!activeMod} onClick={() => setActiveMod('')} testid="equipment-mod-all" />
            {MODALITIES.map(m => (
              <Pill key={m} label={`${m} · ${stats?.by_modality?.[m] ?? 0}`} color={MOD_COLORS[m]}
                active={activeMod === m} onClick={() => setActiveMod(activeMod === m ? '' : m)}
                testid={`equipment-mod-${m}`} />
            ))}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div className="text-center py-16 text-sm text-muted-foreground">Loading equipment…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-muted-foreground" data-testid="equipment-empty">
            No equipment matches.
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="equipment-list">
            {filtered.map(r => (
              <AssetCard key={r.asset_id} r={r} isPM={isPM}
                onEdit={() => setEditing(r)} onDelete={() => onDelete(r)} />
            ))}
          </div>
        )}

      {editing && <EquipmentEditor
        initial={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />}
    </div>
  );
  return embedded ? inner : (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {inner}
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold mt-1" style={{ color }}>{value}</p>
    </div>
  );
}

function Pill({ label, color, active, onClick, testid }) {
  const c = color || E.color;
  return (
    <button onClick={onClick} data-testid={testid}
      className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors"
      style={active
        ? { backgroundColor: `${c}15`, borderColor: `${c}40`, color: c }
        : { borderColor: 'hsl(var(--border))', color: 'hsl(var(--muted-foreground))' }}>
      {label}
    </button>
  );
}

function AssetCard({ r, isPM, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const [logsOpen, setLogsOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [logForm, setLogForm] = useState(false);
  const [qaOpen, setQaOpen] = useState(false);
  const [qa, setQa] = useState([]);
  const c = MOD_COLORS[r.modality] || '#64748B';
  const today = new Date().toISOString().slice(0, 10);
  const overdue = r.next_service_due && r.next_service_due < today && r.status === 'active';

  const loadLogs = async () => {
    const x = await axios.get(`${API}/equipment/${r.asset_id}/service-logs`, { withCredentials: true });
    setLogs(x.data);
  };
  const loadQA = async () => {
    const x = await axios.get(`${API}/qa-tests?asset_id=${r.asset_id}`, { withCredentials: true });
    setQa(x.data);
  };

  const toggleLogs = async () => {
    if (!logsOpen) await loadLogs();
    setLogsOpen(!logsOpen);
  };
  const toggleQa = async () => {
    if (!qaOpen) await loadQA();
    setQaOpen(!qaOpen);
  };

  const onAddLog = async (data, pdfFile) => {
    const r1 = await axios.post(`${API}/equipment/${r.asset_id}/service-logs`, data, { withCredentials: true });
    // If a PDF was dropped on the form, auto-attach it as evidence on the
    // newly-created log entry. Backend returns the inserted log row including
    // log_id; we use that as the parent_id.
    const newLogId = r1.data?.log_id || r1.data?.[0]?.log_id;
    if (pdfFile && newLogId) {
      try {
        const fd = new FormData();
        fd.append('file', pdfFile);
        fd.append('parent_type', 'equipment_service');
        fd.append('parent_id', newLogId);
        fd.append('label', `Service ${data.date} · ${data.type}`);
        await axios.post(`${API}/uploads`, fd, { withCredentials: true });
      } catch (e) {
        // Non-fatal — log entry was created, evidence attach failed
        console.error('PDF evidence attach failed:', e);
      }
    }
    await loadLogs();
    setLogForm(false);
  };
  const recordQA = async (testId, result) => {
    await axios.post(`${API}/qa-tests/${testId}/results`,
      { date: today, result, measured_value: '', notes: '' },
      { withCredentials: true });
    await loadQA();
  };
  const qaOverdueCount = qa.filter(t => t.overdue).length;

  return (
    <div className="bg-card border border-border rounded-2xl p-4 flex flex-col gap-2" data-testid={`equipment-card-${r.asset_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{ backgroundColor: `${c}15`, color: c }}>{r.modality}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
              style={{
                backgroundColor: r.status === 'active' ? '#16A34A15' : r.status === 'under_service' ? '#D9770615' : '#64748B15',
                color: r.status === 'active' ? '#16A34A' : r.status === 'under_service' ? '#D97706' : '#64748B',
              }}>
              {r.status.replace('_', ' ')}
            </span>
            {r.rso_required && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ backgroundColor: '#7C3AED15', color: '#7C3AED' }}>
                <Radiation className="w-2.5 h-2.5" /> RSO
              </span>
            )}
            {overdue && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ backgroundColor: '#DC262615', color: '#DC2626' }}>
                <AlertTriangle className="w-2.5 h-2.5" /> Service overdue
              </span>
            )}
          </div>
          <h3 className="font-['Outfit'] text-base font-semibold text-foreground leading-tight">{r.asset_name}</h3>
          <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground mt-1">
            {r.manufacturer && <span>{r.manufacturer} {r.model && `· ${r.model}`}</span>}
            {r.serial_number && <span className="font-mono">SN: {r.serial_number}</span>}
            {r.location && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {r.location}</span>}
          </div>
        </div>
        {isPM && (
          <div className="flex items-center gap-0.5 shrink-0">
            <button onClick={onEdit} className="p-1.5 text-muted-foreground hover:text-foreground" title="Edit"
              data-testid={`equipment-edit-${r.asset_id}`}><Edit2 className="w-4 h-4" /></button>
            <button onClick={onDelete} className="p-1.5 text-muted-foreground hover:text-red-500" title="Delete"
              data-testid={`equipment-delete-${r.asset_id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] mt-1 border-t border-border pt-2">
        {r.install_date && <div><span className="text-muted-foreground">Install:</span> <span className="text-foreground font-medium">{r.install_date}</span></div>}
        {r.warranty_expiry && <div><span className="text-muted-foreground">Warranty:</span> <span className="text-foreground font-medium">{r.warranty_expiry}</span></div>}
        {r.last_service_at && <div><span className="text-muted-foreground">Last service:</span> <span className="text-foreground font-medium">{r.last_service_at}</span></div>}
        {r.next_service_due && <div><span className="text-muted-foreground">Next service:</span> <span className={`font-medium ${overdue ? 'text-red-500' : 'text-foreground'}`}>{r.next_service_due}</span></div>}
        {r.radiation_licence_no && <div className="col-span-2"><span className="text-muted-foreground">Radiation licence:</span> <span className="text-foreground font-mono">{r.radiation_licence_no}</span> {r.radiation_licence_expiry && <span className="text-muted-foreground">expires {r.radiation_licence_expiry}</span>}</div>}
        {r.lspn_listed && (
          <div className="col-span-2 mt-0.5 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded px-2 py-0.5"
            data-testid={`equipment-lspn-listed-${r.asset_id}`}
            title={`LSPN ${r.lspn_listed.lspn} · ${r.lspn_listed.trading_name} · listed ${r.lspn_listed.lspn_listing_date} · serial on portal: ${r.lspn_listed.lspn_serial}`}>
            <CheckCircle2 className="w-3 h-3" />
            LSPN {r.lspn_listed.lspn} listed · {r.lspn_listed.lspn_listing_date}
          </div>
        )}
      </div>

      {(r.components || []).length > 0 && (
        <div className="border-t border-border pt-2">
          <button onClick={() => setOpen(o => !o)}
            data-testid={`equipment-comp-toggle-${r.asset_id}`}
            className="text-[11px] font-semibold flex items-center gap-1 w-full justify-between hover:text-foreground"
            style={{ color: E.color }}>
            <span>Components ({r.components.length})</span>
            <ChevronDown className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
          {open && (
            <ul className="mt-2 text-xs space-y-1.5">
              {r.components.map((c, i) => (
                <li key={i} className="leading-tight bg-muted/40 rounded-md px-2 py-1.5">
                  <div className="font-semibold text-foreground">{c.label}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">
                    {c.manufacturer && <span>{c.manufacturer} </span>}
                    {c.model && <span>· {c.model} </span>}
                    {c.serial_number && <span>· SN {c.serial_number}</span>}
                  </div>
                  {c.notes && <div className="text-[10px] text-muted-foreground italic mt-0.5">{c.notes}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="border-t border-border pt-2">
        <div className="flex items-center justify-between">
          <button onClick={toggleLogs}
            data-testid={`equipment-logs-toggle-${r.asset_id}`}
            className="text-[11px] font-semibold inline-flex items-center gap-1 hover:text-foreground" style={{ color: E.color }}>
            <Wrench className="w-3 h-3" /> Service log ({logs.length || ''})
            <ChevronDown className={`w-3 h-3 transition-transform ${logsOpen ? 'rotate-180' : ''}`} />
          </button>
          {isPM && logsOpen && (
            <button onClick={() => setLogForm(true)} data-testid={`equipment-log-add-${r.asset_id}`}
              className="text-[10px] font-semibold inline-flex items-center gap-1 px-2 py-0.5 rounded-md border"
              style={{ borderColor: `${E.color}40`, color: E.color }}>
              <Plus className="w-2.5 h-2.5" /> Add entry
            </button>
          )}
        </div>
        {logsOpen && (
          <div className="mt-2 space-y-1.5">
            {logs.length === 0 && <p className="text-[10px] text-muted-foreground italic">No service entries yet.</p>}
            {logs.map(l => (
              <div key={l.log_id} className="bg-muted/40 rounded-md p-2 text-[11px]">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold">{l.date}</span>
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase" style={{ backgroundColor: `${E.color}15`, color: E.color }}>{l.type}</span>
                  {l.technician && <span className="text-muted-foreground">{l.technician}{l.company && ` · ${l.company}`}</span>}
                  {l.next_due && <span className="text-muted-foreground inline-flex items-center gap-1"><Calendar className="w-2.5 h-2.5" /> next {l.next_due}</span>}
                </div>
                <div className="text-foreground mt-1">{l.summary}</div>
                {l.findings && <div className="text-muted-foreground italic mt-0.5 text-[10px]">{l.findings}</div>}
                <div className="mt-2">
                  <EvidenceUploads parentType="equipment_service" parentId={l.log_id}
                    label={`Service ${l.date} · ${l.type}`}
                    testidPrefix={`service-evidence-${l.log_id}`} />
                </div>
              </div>
            ))}
          </div>
        )}
        {logForm && (
          <ServiceLogForm onCancel={() => setLogForm(false)} onSubmit={onAddLog} />
        )}
      </div>

      {/* QA test schedule */}
      <div className="border-t border-border pt-2">
        <button onClick={toggleQa}
          data-testid={`equipment-qa-toggle-${r.asset_id}`}
          className="text-[11px] font-semibold inline-flex items-center gap-1 hover:text-foreground" style={{ color: E.color }}>
          <ClipboardCheck className="w-3 h-3" /> QA schedule {qa.length > 0 && `(${qa.length})`}
          {qaOpen && qaOverdueCount > 0 && <span className="text-red-500">· {qaOverdueCount} overdue</span>}
          <ChevronDown className={`w-3 h-3 transition-transform ${qaOpen ? 'rotate-180' : ''}`} />
        </button>
        {qaOpen && (
          <div className="mt-2 space-y-1.5">
            {qa.length === 0 && <p className="text-[10px] text-muted-foreground italic">No QA tests defined for this asset.</p>}
            {qa.map(t => (
              <div key={t.test_id} className={`bg-muted/40 rounded-md p-2 text-[11px] ${t.overdue ? 'border-l-2 border-red-500' : ''}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-semibold text-foreground">{t.name}</span>
                      <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ backgroundColor: `${E.color}15`, color: E.color }}>{t.frequency}</span>
                      {t.never_run && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Never run</span>}
                      {t.overdue && !t.never_run && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-500/10 text-red-600">Overdue</span>}
                      {t.due_soon && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-600">Due ≤3d</span>}
                      {!t.overdue && !t.due_soon && t.last_completed_at && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-green-500/10 text-green-600">Current</span>}
                    </div>
                    {t.description && <p className="text-[10px] text-muted-foreground italic mt-0.5">{t.description}</p>}
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      {t.last_completed_at ? `Last: ${t.last_completed_at} · ${t.last_result || '—'}` : 'No runs yet'}
                      {t.next_due && ` · Next due ${t.next_due}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => recordQA(t.test_id, 'pass')} data-testid={`qa-pass-${t.test_id}`}
                      title="Record a pass result for today" className="text-[9px] font-bold uppercase px-2 py-1 rounded-md border border-green-500/40 text-green-600 hover:bg-green-500/10">
                      Pass
                    </button>
                    <button onClick={() => recordQA(t.test_id, 'fail')} data-testid={`qa-fail-${t.test_id}`}
                      title="Record a fail result for today" className="text-[9px] font-bold uppercase px-2 py-1 rounded-md border border-red-500/40 text-red-600 hover:bg-red-500/10">
                      Fail
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {r.notes && (
        <div className="border-t border-border pt-2 text-[11px] text-muted-foreground italic flex items-start gap-1">
          <FileText className="w-3 h-3 mt-0.5 shrink-0" /> {r.notes}
        </div>
      )}
    </div>
  );
}

function ServiceLogForm({ onCancel, onSubmit }) {
  const [f, setF] = useState({ date: new Date().toISOString().slice(0, 10), technician: '', company: '', type: 'preventive', summary: '', findings: '', next_due: '' });
  const [pdfFile, setPdfFile] = useState(null);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));
  const acceptExtraction = (extracted, file) => {
    setF(s => ({
      ...s,
      date:       extracted.date       || s.date,
      type:       (extracted.type === 'preventative' ? 'preventive' : extracted.type) || s.type,
      technician: extracted.technician || s.technician,
      company:    extracted.company    || s.company,
      summary:    extracted.summary    || s.summary,
      findings:   extracted.findings   || s.findings,
      next_due:   extracted.next_due   || s.next_due,
    }));
    setPdfFile(file);
  };
  const submit = () => f.summary.trim() && onSubmit(f, pdfFile);
  return (
    <div className="mt-2 border border-border rounded-lg p-2 space-y-1.5 bg-muted/30">
      <PDFIntakeDropzone intakeType="equipment_service"
        label="Drop PDF service report — AI auto-fills the form"
        color={E.color} testidPrefix="service-pdf-intake"
        onExtracted={acceptExtraction} />

      <div className="grid grid-cols-2 gap-1.5">
        <label className="block">
          <span className="text-[9px] font-bold uppercase text-muted-foreground">Date</span>
          <DatePickerField value={f.date} onChange={v => set('date', v)} format="iso" />
        </label>
        <label className="block">
          <span className="text-[9px] font-bold uppercase text-muted-foreground">Type</span>
          <select value={f.type} onChange={e => set('type', e.target.value)} className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5">
            {['preventive', 'corrective', 'calibration', 'qa_test'].map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <input placeholder="Technician" value={f.technician} onChange={e => set('technician', e.target.value)} className="text-xs bg-background border border-border rounded-md px-2 py-1.5" />
        <input placeholder="Company" value={f.company} onChange={e => set('company', e.target.value)} className="text-xs bg-background border border-border rounded-md px-2 py-1.5" />
      </div>
      <input placeholder="Summary *" value={f.summary} onChange={e => set('summary', e.target.value)} className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5" />
      <textarea placeholder="Findings" value={f.findings} onChange={e => set('findings', e.target.value)} rows={2} className="w-full text-xs bg-background border border-border rounded-md px-2 py-1.5" />
      <label className="block">
        <span className="text-[9px] font-bold uppercase text-muted-foreground">Next service due</span>
        <DatePickerField value={f.next_due} onChange={v => set('next_due', v)} format="iso" />
      </label>
      {pdfFile && (
        <div className="text-[10px] text-muted-foreground italic">
          📎 {pdfFile.name} will be attached as evidence on save.
        </div>
      )}
      <div className="flex justify-end gap-1.5">
        <button onClick={onCancel} className="text-xs px-2 py-1 rounded-md border border-border">Cancel</button>
        <button onClick={submit} disabled={!f.summary.trim()}
          className="text-xs font-semibold px-3 py-1 rounded-md text-white disabled:opacity-50"
          style={{ backgroundColor: E.color }}>Save log entry</button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Editor modal
// ─────────────────────────────────────────────────────────────────────────────

const blankComponent = () => ({ label: '', manufacturer: '', model: '', serial_number: '', notes: '' });

function EquipmentEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(() => initial
    ? { ...initial, components: [...(initial.components || [])] }
    : {
      asset_name: '', modality: 'CT', manufacturer: '', model: '', serial_number: '',
      location: '', install_date: '', warranty_expiry: '', last_service_at: '', next_service_due: '',
      last_calibration_at: '', next_calibration_due: '',
      rso_required: false, radiation_licence_no: '', radiation_licence_expiry: '',
      status: 'active', components: [], notes: '', document_urls: [],
    });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setComp = (i, k, v) => setForm(f => ({ ...f, components: f.components.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  const addComp = () => setForm(f => ({ ...f, components: [...f.components, blankComponent()] }));
  const removeComp = (i) => setForm(f => ({ ...f, components: f.components.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setErr('');
    if (!form.asset_name.trim()) { setErr('Asset name is required.'); return; }
    setSaving(true);
    try {
      if (initial?.asset_id) {
        await axios.put(`${API}/equipment/${initial.asset_id}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/equipment`, form, { withCredentials: true });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="equipment-editor">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background rounded-2xl border border-border w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-background border-b border-border px-5 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-bold text-foreground">{initial ? 'Edit equipment' : 'New equipment'}</h3>
          <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          {err && <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-500 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />{err}
          </div>}

          {!initial && (
            <PDFIntakeDropzone intakeType="equipment_install"
              label="Drop install / commissioning PDF — AI auto-fills fields"
              color={E.color} testidPrefix="install-pdf-intake"
              onExtracted={(ex) => setForm(f => ({
                ...f,
                asset_name:    ex.asset_name    || f.asset_name,
                modality:      ex.modality      || f.modality,
                manufacturer:  ex.manufacturer  || f.manufacturer,
                model:         ex.model         || f.model,
                serial_number: ex.serial_number || f.serial_number,
                install_date:  ex.install_date  || f.install_date,
                warranty_expiry: ex.warranty_expiry || f.warranty_expiry,
                location:      ex.location      || f.location,
                notes:         ex.notes         || f.notes,
              }))} />
          )}

          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><F label="Asset name *" value={form.asset_name} onChange={v => set('asset_name', v)} testid="eq-name" /></div>
            <FSel label="Modality" value={form.modality} onChange={v => set('modality', v)} options={MODALITIES} testid="eq-modality" />
            <FSel label="Status" value={form.status} onChange={v => set('status', v)} options={STATUSES} testid="eq-status" />
            <F label="Manufacturer" value={form.manufacturer} onChange={v => set('manufacturer', v)} testid="eq-manufacturer" />
            <F label="Model" value={form.model} onChange={v => set('model', v)} testid="eq-model" />
            <F label="Serial number" value={form.serial_number} onChange={v => set('serial_number', v)} testid="eq-serial" />
            <F label="Location / Room" value={form.location} onChange={v => set('location', v)} testid="eq-location" />
            <FDate label="Install date" value={form.install_date} onChange={v => set('install_date', v)} testid="eq-install" />
            <FDate label="Warranty expiry" value={form.warranty_expiry} onChange={v => set('warranty_expiry', v)} testid="eq-warranty" />
            <FDate label="Last service" value={form.last_service_at} onChange={v => set('last_service_at', v)} testid="eq-last-service" />
            <FDate label="Next service due" value={form.next_service_due} onChange={v => set('next_service_due', v)} testid="eq-next-service" />
            <FDate label="Last calibration" value={form.last_calibration_at} onChange={v => set('last_calibration_at', v)} testid="eq-last-cal" />
            <FDate label="Next calibration due" value={form.next_calibration_due} onChange={v => set('next_calibration_due', v)} testid="eq-next-cal" />
            <div className="col-span-2 flex items-center gap-2 pt-2 border-t border-border">
              <input id="rso-required" type="checkbox" checked={form.rso_required}
                onChange={e => set('rso_required', e.target.checked)}
                data-testid="eq-rso-required" className="w-4 h-4" />
              <label htmlFor="rso-required" className="text-xs font-semibold text-foreground inline-flex items-center gap-1.5">
                <Radiation className="w-3.5 h-3.5" style={{ color: '#7C3AED' }} /> Radiation device — RSO oversight required
              </label>
            </div>
            {form.rso_required && (
              <>
                <F label="Radiation licence #" value={form.radiation_licence_no} onChange={v => set('radiation_licence_no', v)} testid="eq-rad-lic" />
                <FDate label="Licence expiry" value={form.radiation_licence_expiry} onChange={v => set('radiation_licence_expiry', v)} testid="eq-rad-lic-exp" />
              </>
            )}
          </div>

          <div className="border-t border-border pt-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Sub-components</span>
              <button onClick={addComp} type="button" data-testid="eq-comp-add"
                className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded-md border"
                style={{ borderColor: `${E.color}40`, color: E.color }}>
                <Plus className="w-3 h-3" /> Add component
              </button>
            </div>
            <div className="space-y-2">
              {form.components.length === 0 && <p className="text-xs text-muted-foreground italic">No sub-components added.</p>}
              {form.components.map((c, i) => (
                <div key={i} className="border border-border rounded-lg p-2 grid grid-cols-2 gap-2 relative" data-testid={`eq-comp-${i}`}>
                  <button type="button" onClick={() => removeComp(i)}
                    className="absolute top-1 right-1 p-1 text-muted-foreground hover:text-red-500"
                    data-testid={`eq-comp-remove-${i}`}>
                    <Trash2 className="w-3 h-3" />
                  </button>
                  <div className="col-span-2"><F label="Label *" value={c.label} onChange={v => setComp(i, 'label', v)} testid={`eq-comp-label-${i}`} /></div>
                  <F label="Manufacturer" value={c.manufacturer} onChange={v => setComp(i, 'manufacturer', v)} testid={`eq-comp-mfr-${i}`} />
                  <F label="Model" value={c.model} onChange={v => setComp(i, 'model', v)} testid={`eq-comp-model-${i}`} />
                  <F label="Serial number" value={c.serial_number} onChange={v => setComp(i, 'serial_number', v)} testid={`eq-comp-serial-${i}`} />
                  <F label="Notes" value={c.notes} onChange={v => setComp(i, 'notes', v)} testid={`eq-comp-notes-${i}`} />
                </div>
              ))}
            </div>
          </div>

          <div><F label="Notes" value={form.notes} onChange={v => set('notes', v)} testid="eq-notes" type="textarea" /></div>
        </div>
        <div className="sticky bottom-0 bg-background border-t border-border px-5 py-3 flex justify-end gap-2 z-10">
          <button onClick={onClose} className="text-sm px-3 py-1.5 rounded-lg border border-border">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="eq-save-btn"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-1.5 rounded-lg text-white disabled:opacity-50"
            style={{ backgroundColor: E.color }}>
            <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : (initial ? 'Save changes' : 'Add equipment')}
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, value, onChange, testid, type = 'text' }) {
  const cls = "w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2";
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      {type === 'textarea'
        ? <textarea value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} rows={2} />
        : <input type="text" value={value ?? ''} onChange={e => onChange(e.target.value)} data-testid={testid} className={cls} />}
    </label>
  );
}

function FSel({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <select value={value || ''} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full mt-1 text-sm bg-background border border-border rounded-lg px-3 py-2">
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function FDate({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</span>
      <DatePickerField value={value} onChange={onChange} format="iso" testid={testid} />
    </label>
  );
}



// ─────────────────────────────────────────────────────────────────────────
// LSPN reconcile — admin-only. Two-step flow:
//   1. Drop a fresh LSPN Equipment Portal Report PDF → backend extracts
//      via GPT-5.2 + fuzzy-matches against existing assets by serial.
//   2. Preview the matches (asset → portal serial, change indicator).
//      Click "Apply" to write the updates.
// ─────────────────────────────────────────────────────────────────────────
function LspnReconcileBar({ onApplied }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const reset = () => { setPreview(null); setError(''); };

  const upload = async (apply) => {
    const f = fileRef.current?.files?.[0] || preview?._file;
    if (!f) { setError('Select a PDF first.'); return; }
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('apply', apply ? 'true' : 'false');
      const r = await axios.post(`${API}/equipment/lspn-reconcile/pdf`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000,
      });
      // Stash the file on the preview so "Apply" doesn't need re-upload
      setPreview({ ...r.data, _file: f });
      if (apply && r.data.applied) {
        onApplied?.();
      }
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <div className="mb-5 flex justify-end">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="lspn-reconcile-open-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-dashed border-border text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        >
          <Upload className="w-3.5 h-3.5" />
          LSPN reconcile…
        </button>
      </div>
    );
  }

  return (
    <div className="mb-5 border border-dashed border-emerald-500/40 rounded-xl p-4 bg-emerald-500/5" data-testid="lspn-reconcile-bar">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-semibold text-foreground flex items-center gap-2">
            <Upload className="w-4 h-4 text-emerald-600" />
            LSPN Equipment Portal reconcile
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-2xl">
            Drop a fresh LSPN portal export (PDF). GPT-5.2 will extract the equipment list, fuzzy-match against your asset register by serial number, and show a dry-run preview before applying. Idempotent.
          </p>
        </div>
        <button onClick={() => { setOpen(false); reset(); }} className="text-muted-foreground hover:text-foreground" data-testid="lspn-reconcile-close-btn">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf"
          onChange={() => { setPreview(null); setError(''); }}
          disabled={busy}
          className="text-xs file:px-3 file:py-1 file:rounded-md file:border-0 file:bg-emerald-600 file:text-white file:font-semibold file:cursor-pointer disabled:opacity-50"
          data-testid="lspn-reconcile-file"
        />
        <button
          type="button"
          onClick={() => upload(false)}
          disabled={busy}
          data-testid="lspn-reconcile-preview-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
          {busy ? 'Extracting…' : 'Preview match'}
        </button>
      </div>

      {error && (
        <div className="text-[11px] text-red-600 dark:text-red-400 bg-red-500/10 border border-red-500/30 rounded px-2 py-1 mb-2" data-testid="lspn-reconcile-error">{error}</div>
      )}

      {preview && (
        <div className="bg-card border border-border rounded-lg p-3" data-testid="lspn-reconcile-preview">
          <div className="text-xs text-muted-foreground mb-2">
            Portal: <span className="font-semibold text-foreground">LSPN {preview.lspn}</span> · {preview.trading_name} · listed {preview.listing_date} · {preview.total_portal} row(s)
          </div>
          <table className="w-full text-[11px]">
            <thead className="text-left text-muted-foreground">
              <tr><th className="py-1">Asset</th><th>Current SN</th><th>Portal SN</th><th>Match</th><th>Change?</th></tr>
            </thead>
            <tbody>
              {(preview.matches || []).map((m, i) => (
                <tr key={i} className="border-t border-border" data-testid={`lspn-reconcile-match-${i}`}>
                  <td className="py-1 font-mono">{m.asset_id}</td>
                  <td className="font-mono">{m.current_serial}</td>
                  <td className={`font-mono ${m.is_change ? 'text-amber-600 dark:text-amber-400 font-bold' : ''}`}>{m.portal_serial}</td>
                  <td>{Math.round(m.similarity * 100)}%</td>
                  <td>{m.is_change ? <span className="text-amber-600 dark:text-amber-400 font-semibold">↻ replaces</span> : <span className="text-emerald-600 dark:text-emerald-400">✓ same</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.unmatched?.length > 0 && (
            <div className="mt-2 text-[11px] text-amber-700 dark:text-amber-300">
              <strong>{preview.unmatched.length} unmatched row(s)</strong> — won't be applied: {preview.unmatched.map(u => u.portal_serial).join(', ')}
            </div>
          )}
          <div className="mt-3 flex items-center gap-2">
            {preview.applied ? (
              <span className="text-xs text-emerald-700 dark:text-emerald-300 font-semibold inline-flex items-center gap-1" data-testid="lspn-reconcile-applied">
                <CheckCircle2 className="w-3.5 h-3.5" /> Applied — {preview.updated} asset(s) updated.
              </span>
            ) : (
              <button
                type="button"
                onClick={() => upload(true)}
                disabled={busy || !preview.matches?.length}
                data-testid="lspn-reconcile-apply-btn"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white disabled:opacity-50"
              >
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                Apply {preview.matches?.length || 0} update(s)
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
